<template>
  <q-toolbar>
    <q-toolbar-title class="text-primary">TUS Calculator usage</q-toolbar-title>
  </q-toolbar>
  <q-card-section>
    <div class="flex justify-center row q-gutter-sm">
      <q-select
        class="analytics-filter-input"
        dense
        :dark="isDarkModeGlobal"
        outlined
        label="Group by"
        :options="['day', 'week', 'month', 'year']"
        id="grouping"
        v-model="grouping"
        @change="updateChart"
      />
      <q-input
        class="analytics-filter-input"
        dense
        :dark="isDarkModeGlobal"
        outlined
        label="Start Date"
        type="date"
        id="startDate"
        :rules="[value => value <= endDate || 'Start Date must lie before end date']"
        v-model="startDate"
        @change="updateChart"
      />
      <q-input
        class="analytics-filter-input"
        dense
        :dark="isDarkModeGlobal"
        outlined
        label="End Date"
        type="date"
        id="endDate"
        :rules="[value => value >= startDate || 'End date must lie after start date']"
        v-model="endDate"
        @change="updateChart"
      />
      <div
        class="flex justify-center"
        v-if="chartError.length > 0"
      >
        {{ chartError }}
      </div>
      <div
        ref="timestampChart"
        style="width: 600vw; height: 500px;"
      />
    </div>
  </q-card-section>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref, watch } from 'vue';
import * as echarts from 'echarts';
import { useCalculatorStore } from '../stores/calculator.store'
import {storeToRefs} from "pinia"
const calculatorStore = useCalculatorStore()
const {
  isDarkModeGlobal
} = storeToRefs(calculatorStore)
const trackingApiUrl = import.meta.env.VITE_APP_TRACKING_API;
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
interface EventData {
  id: number;
  timestamp: string;
}

const events = ref([]);

const timestampChart = ref<HTMLDivElement | null>(null);
const grouping = ref<'day' | 'week' | 'month' | 'year'>('day'); // Default grouping by day
const chartInstance = reactive({ instance: null as echarts.ECharts | null });
const chartError = ref('');

const fetchData = async () : Promise<EventData[]> => {
  let apiUrl = `?group_by=${grouping.value}&`;
  if (startDate.value || endDate.value) {
      const params = new URLSearchParams();
      if (startDate.value) params.append("start_date", startDate.value);
      if (endDate.value) params.append("end_date", endDate.value);
      apiUrl += `${params.toString()}`;
  }

  if ((startDate.value && endDate.value) && (new Date(startDate.value) > new Date(endDate.value))) {
    throw Error('Invalid date range');
  }

  try {
    const response = await fetch(`${trackingApiUrl}${apiUrl}`, {
      method: "GET",
      mode: "cors"
    });
    const data = await response.json();
    return data.events;
  } catch (error) {
    chartError.value = "Could not fetch analytics data. Please contact the TSG at tsg@ru.nl";
    throw Error(error);
  }

};

const updateChart = async () => {
  let xAxisName = 'Year'
  if (grouping.value === 'day') {
    xAxisName = 'Date'
  } else if (grouping.value === 'week') {
    xAxisName = 'Week Start'
  }

  if (chartInstance.instance && timestampChart.value) {
    try {
      events.value = await fetchData()
      const options: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
      },
      xAxis: {
        type: 'category',
        data: events.value.map(ev => ev.group),
        name: xAxisName,
      },
      yAxis: {
        type: 'value',
        name: 'Event Count',
      },
      series: [
        {
          name: 'Site Usage',
          type: 'line',
          data: events.value.map(ev => ev.count),
          smooth: true,
        },
      ],
    };

    chartInstance.instance.setOption(options);
    } catch (e) {
      console.error(e)
    }
  }
};

onMounted(() => {
  if (timestampChart.value) {
    chartInstance.instance = echarts.init(timestampChart.value);

    // Subtract 7 days to get the date for one week earlier
    const oneWeekEarlier = new Date();
    oneWeekEarlier.setDate(oneWeekEarlier.getDate() - 7);

    const yearS = oneWeekEarlier.getFullYear();
    const monthS = String(oneWeekEarlier.getMonth() + 1).padStart(2, '0');
    const dayS = String(oneWeekEarlier.getDate()).padStart(2, '0');

    startDate.value = `${yearS}-${monthS}-${dayS}`;

    const date = new Date()
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed, so add 1
    const day = String(date.getDate()).padStart(2, '0');

    endDate.value = `${year}-${month}-${day}`

    updateChart();
    window.addEventListener('resize', () => chartInstance.instance?.resize());
  }
});

watch(grouping, updateChart);
</script>

<style>
.analytics-filter-input {
  width: 120px;
}
</style>